import React, { memo, useCallback, useEffect, useState } from "react";
import CommonLayout from "../../../../layouts/commonLayout/CommonLayout";
import Container from "../../../../layouts/containers/Container";
import baseUrl from "../../../../api/baseUrl";
import AddPurpose from "./AddPurpose";
import TabBar from "../../../../components/tabbar/TabBar";
import { useDispatch } from "react-redux";
import { setPurpose } from "../../../../redux/appointment/appointmentAction";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import FlagIcon from "@mui/icons-material/Flag";
import SearchIcon from "@mui/icons-material/Search";

interface Purpose {
  _id: string;
  purposeName: string;
}

interface TableRowProps {
  item: Purpose;
  index: number;
  onUpdate: (id: string) => void;
}

const TableRow: React.FC<TableRowProps> = memo(({ item, index, onUpdate }) => (
  <tr className="hover:bg-slate-50/50 transition-colors">
    <td className="px-6 py-3">
      <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-slate-100 text-slate-600">
        {index + 1}
      </span>
    </td>
    <td className="px-6 py-3">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white">
          <FlagIcon sx={{ fontSize: 18 }} />
        </div>
        <span className="font-medium text-slate-800">{item.purposeName}</span>
      </div>
    </td>
    <td className="px-6 py-3">
      <button
        onClick={() => onUpdate(item._id)}
        className="p-2 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
        title="Edit"
      >
        <EditIcon sx={{ fontSize: 18 }} />
      </button>
    </td>
  </tr>
));

const MobileCard: React.FC<TableRowProps> = memo(({ item, index, onUpdate }) => (
  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-white">
          <FlagIcon sx={{ fontSize: 20 }} />
        </div>
        <div>
          <p className="font-semibold text-slate-800">{item.purposeName}</p>
          <span className="text-xs text-slate-400">#{index + 1}</span>
        </div>
      </div>
      <button
        onClick={() => onUpdate(item._id)}
        className="p-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors"
      >
        <EditIcon sx={{ fontSize: 18 }} />
      </button>
    </div>
  </div>
));

const ManagePurpose: React.FC = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [data, setData] = useState<Purpose[]>([]);
  const [id, setId] = useState<string>("");
  const [search, setSearch] = useState("");
  const dispatch = useDispatch();

  const getData = useCallback(() => {
    baseUrl
      .get("client/setting/purpose/findAllPurpose")
      .then((response) => {
        setData(response.data.data);
        dispatch(setPurpose(response.data.data, false));
      })
      .catch((error) => console.error(error?.response));
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  const closeModal = useCallback(() => {
    setOpen(false);
    getData();
  }, [getData]);

  const openModal = useCallback(() => setOpen(true), []);
  
  const update = useCallback((id: string) => {
    setId(id);
    setOpen(true);
  }, []);

  const filteredData = data.filter((item) =>
    item.purposeName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>

      {/* Header Card */}
      <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 p-4 mt-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <SearchIcon
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              sx={{ fontSize: 20 }}
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search purpose..."
              className="w-full pl-12 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
            />
          </div>

          <div className="flex items-center gap-4">
            {/* Count Badge */}
            <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-xl">
              <FlagIcon className="text-amber-500" sx={{ fontSize: 20 }} />
              <span className="font-bold text-amber-600">{data.length}</span>
              <span className="text-slate-500 text-sm">Purposes</span>
            </div>

            {/* Add Button */}
            <button
              onClick={() => openModal()}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary to-primaryLight text-white rounded-xl hover:shadow-lg hover:shadow-primary/20 transition-all font-medium"
            >
              <AddIcon sx={{ fontSize: 20 }} />
              Add Purpose
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-100 overflow-hidden mt-4">
        {/* Mobile Cards */}
        <div className="md:hidden p-4 space-y-3">
          {filteredData.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <FlagIcon sx={{ fontSize: 48 }} className="mb-2" />
              <p>No purposes found</p>
            </div>
          ) : (
            filteredData.map((item, i) => (
              <MobileCard key={item._id} item={item} index={i} onUpdate={update} />
            ))
          )}
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/80">
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  No
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Purpose Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-12 text-center text-slate-400">
                    <FlagIcon sx={{ fontSize: 48 }} className="mb-2" />
                    <p>No purposes found</p>
                  </td>
                </tr>
              ) : (
                filteredData.map((item, i) => (
                  <TableRow key={item._id} item={item} index={i} onUpdate={update} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {open && <AddPurpose id={id} closeModal={closeModal} />}
    </>
  );
};

export default ManagePurpose;
